import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { StickyHeaderNavigation } from '@/components'
import { LinearGradient } from '@/components/LinearGradient'
import { ChevronDown } from '@/assets/icons'
import {
  AddMoneyAmountDisplay,
  AddMoneyConversionRate,
  AddMoneyNumberPad,
  AddMoneyButtonConfirmation,
  ResetAddMoneyOnRouteEntry,
  CurrencyBottomSheet,
} from '@/features/add-money'
import { useAddMoneyStore } from '@/store/useAddMoneyStore'
import { useGetFeaturesSettings, useGetWallet } from '@/domain'
import { useUserStore } from '@/store/useUserStore'
import { useTheme } from '@/hooks/useTheme'
import useOffsets from '@/hooks/useOffsets'

// Local component for small currency icon
interface SmallCurrencyIconProps {
  uri?: string
  bgColor: string | [string, string, ...string[]]
  size?: number
  fallbackText?: string
}

function SmallCurrencyIcon({
  uri,
  bgColor,
  size = 24,
  fallbackText,
}: SmallCurrencyIconProps) {
  const gradientColors = Array.isArray(bgColor) ? bgColor : [bgColor, bgColor]
  const finalColors =
    gradientColors.length === 1
      ? [gradientColors[0], gradientColors[0]]
      : gradientColors

  return (
    <LinearGradient
      colors={finalColors}
      locations={finalColors.length === 3 ? [0, 0.15, 0.8] : [0, 1]}
      start={{ x: 0.8, y: 0 }}
      end={{ x: 0.5, y: 1 }}
      circle
      circleSize={size}
      center
    >
      {uri ? (
        <img
          src={uri}
          alt={fallbackText || 'Currency'}
          style={{
            width: '100%',
            height: `16px`, // Reduced height for small size
            objectFit: 'contain',
          }}
          onError={(e) => {
            const target = e.target as HTMLImageElement
            target.style.display = 'none'
          }}
        />
      ) : (
        <BaseText
          fontSize={Math.max(10, size / 2.5)}
          fontFamily="semibold"
          color="white"
          style={{
            textAlign: 'center',
            fontWeight: '600',
          }}
        >
          {fallbackText || '?'}
        </BaseText>
      )}
    </LinearGradient>
  )
}

export default function AddMoneyScreen() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const offsets = useOffsets()
  const { setSelectedCurrency, selectedCurrency } = useAddMoneyStore()

  // Store state for modal
  const isCurrencyModalOpen = useAddMoneyStore(
    (state) => state.isCurrencyModalOpen,
  )
  const setIsCurrencyModalOpen = useAddMoneyStore(
    (state) => state.setIsCurrencyModalOpen,
  )

  // Get wallet data and feature settings
  const { data: walletData } = useGetWallet()
  const { data: featureSettings } = useGetFeaturesSettings()
  const completeCurrencies = useUserStore((s) => s.wallet?.completeCurrencies)

  // Get currency data from location.state
  const currencyFromState =
    location.state?.currency || location.state?.selectedCurrency

  // Filter currencies by feature settings
  const availableSelectorCurrencies =
    featureSettings?.features.on_ramp.currencies?.items

  // Get current currencies from wallet
  const allCurrencies =
    walletData?.completeCurrencies || completeCurrencies || []

  // Filter currencies by available on_ramp methods
  const completeCurrenciesFiltered = availableSelectorCurrencies
    ? allCurrencies.filter((currency) =>
        availableSelectorCurrencies?.includes(currency.code),
      )
    : allCurrencies

  // Fallback: if no currency selected, set USDT as default
  // BUT only if there is no currency in location.state (otherwise ResetAddMoneyOnRouteEntry will set it)
  useEffect(() => {
    // Wait for ResetAddMoneyOnRouteEntry to process currencyFromState
    if (
      !selectedCurrency &&
      !currencyFromState &&
      completeCurrenciesFiltered.length > 0
    ) {
      const defaultCurrency =
        completeCurrenciesFiltered.find((c) => c.code === 'USDT') ||
        completeCurrenciesFiltered[0]
      if (defaultCurrency) {
        setSelectedCurrency(defaultCurrency)
      }
    }
  }, [
    selectedCurrency,
    currencyFromState,
    completeCurrenciesFiltered,
    setSelectedCurrency,
  ])

  // Handlers for currency selection modal
  const handleCurrencySelect = () => {
    setIsCurrencyModalOpen(true)
  }

  const handleCloseCurrencyModal = () => {
    setIsCurrencyModalOpen(false)
  }

  const handleBackPress = () => {
    navigate('/add-money/select-deposit-type')
  }

  return (
    <>
      <ResetAddMoneyOnRouteEntry
        cryptoCode={currencyFromState?.code || currencyFromState?.cryptoCode}
      />

      <BaseView
        flex={1}
        bgColor="background.screen"
        style={{
          height: '100vh',
          paddingTop: offsets.top + 70,
        }}
      >
        {/* Fixed Header Navigation */}
        <StickyHeaderNavigation
          title={t('form:actions.addMoney')}
          onBackPress={handleBackPress}
        />

        {/* Content - structure as in RN version */}
        <BaseView
          flex={1}
          px={12}
          // pt={70}
          // pb={16}
          className="flex flex-col h-full"
          // style={{ minHeight: 'calc(100vh - 70px)' }}
        >
          {/* Central area with amount display */}
          <BaseView
            align="center"
            gap={14}
            className="flex-1 flex flex-col justify-start"
            style={
              {
                // paddingTop: 28,
                // paddingBottom: 20,
              }
            }
          >
            <BaseView gap={20} center>
              <AddMoneyAmountDisplay />
              <AddMoneyConversionRate />
            </BaseView>

            {/* Currency Selector */}
            <BaseView center mb={20}>
              <BaseView
                onClick={handleCurrencySelect}
                className="px-4 py-3 rounded-full cursor-pointer hover:opacity-80 transition-opacity duration-200"
                style={{
                  backgroundColor: theme.colors.background.cardItemRoot,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {/* Currency icon */}
                <SmallCurrencyIcon
                  uri={selectedCurrency?.icon}
                  bgColor={selectedCurrency?.avatarBGRefColor || '#ccc'}
                  size={24}
                  fallbackText={selectedCurrency?.code?.substring(0, 2) || '?'}
                />

                {/* Currency code */}
                <BaseText
                  fontSize={16}
                  fontFamily="semibold"
                  color="label.default"
                >
                  {selectedCurrency?.code}
                </BaseText>

                {/* Down arrow */}
                <ChevronDown size={16} color={theme.colors.label.default} />
              </BaseView>
            </BaseView>
          </BaseView>

          {/* Bottom area with keyboard and button - fixed at bottom */}
          <BaseView
            className="flex-shrink-0"
            style={{
              gap: 12,
              // paddingBottom: 20,
            }}
          >
            <AddMoneyNumberPad />
            <div style={{ paddingBottom: offsets.bottom }}>
              <AddMoneyButtonConfirmation />
            </div>
          </BaseView>
        </BaseView>
      </BaseView>

      {/* Currency Selection Bottom Sheet */}
      <CurrencyBottomSheet
        isOpen={isCurrencyModalOpen}
        onClose={handleCloseCurrencyModal}
      />
    </>
  )
}
